<template>
  <div
    class="s-userAvatar__wrapper"
    :class="showOnlineStatus && `s-userAvatar__wrapper-${onlineStatusText}`">
    <img
      :src="avatar"
      :alt="$t('generic.userAvatar')"
      class="s-userAvatar__image"
      :class="{ '-small': small }"
      :style="imageStyle"
      @error="handleLoadError" />
    <div
      v-if="showOnlineStatus"
      class="s-userAvatar__statusDot"
      :style="dotStyle"
      role="status"
      aria-live="polite"
      :title="onlineStatusToTranslatedText"
      :aria-label="onlineStatusToTranslatedText">
      <span class="s-screen__readerOnly">
        {{ onlineStatusToTranslatedText }}
      </span>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import { useOnlineUserSessionStore } from '@/modules/account/onlineUserSessionStore'

export default {
  props: {
    showOnlineStatus: {
      type: Boolean,
      default: false,
    },
    avatarUrl: {
      type: String,
      default: null,
    },
    small: {
      type: Boolean,
      default: false,
    },
    isOnline: {
      type: Boolean,
      default: null,
    },
    size: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      reloadedAvatar: false,
    }
  },
  setup(props) {
    if (!props.showOnlineStatus || props.isOnline !== null) {
      return {}
    }
    const onlineUserSessionStore = useOnlineUserSessionStore()
    return { onlineUserSessionStore }
  },
  computed: {
    ...mapGetters({
      currentUserAvatar: 'auth/avatar',
    }),
    avatar() {
      if (this.avatarUrl) {
        return this.avatarUrl
      }
      return this.currentUserAvatar
    },
    isMemberAvatar() {
      return Boolean(this.avatarUrl)
    },
    onlineStatusText() {
      if (!this.showOnlineStatus) {
        return ''
      }
      if (this.isOnline !== null) {
        return this.isOnline ? 'online' : 'offline'
      }
      return this.onlineUserSessionStore.isOnline ? 'online' : 'offline'
    },
    imageStyle() {
      if (!this.size) {
        return {}
      }
      return {
        width: `${this.size}px`,
        height: `${this.size}px`,
        borderRadius: `${this.size}px`,
      }
    },
    dotStyle() {
      if (!this.size) {
        return {}
      }
      const dotSize = Math.round(this.size * 0.35)
      return {
        width: `${dotSize}px`,
        height: `${dotSize}px`,
      }
    },
    onlineStatusToTranslatedText() {
      if (!this.showOnlineStatus) {
        return ''
      }
      return this.$t(`generic.${this.onlineStatusText}`)
    },
  },
  methods: {
    handleLoadError() {
      if (this.reloadedAvatar || this.isMemberAvatar) {
        return
      }
      this.reloadedAvatar = true
      this.$store.dispatch('auth/updateProfile')
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/01_settings/_colors.scss';

.s-userAvatar__wrapper {
  position: relative;
  display: inline-block;
}

.s-userAvatar__wrapper-online .s-userAvatar__statusDot {
  background-color: $color-success;
}

.s-userAvatar__wrapper-offline .s-userAvatar__statusDot {
  background-color: $color-gray;
}

.s-userAvatar__image {
  border-radius: 32px;
  width: 48px;
  height: 48px;
  display: block;

  &.-small {
    width: 32px;
    height: 32px;
  }
}

.s-userAvatar__statusDot {
  position: absolute;
  border: 2px solid $color-white;
  border-radius: 32px;
  width: 14px;
  height: 14px;
  bottom: 2px;
  right: 0;
}

.s-screen__readerOnly {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
</style>
